import PhoneCallLink from "@/components/atoms/PhoneCallLink";
import UnknownBadge from "@/components/atoms/UnknownBadge";
import AllInstructorsHeader from "@/components/molecules/table-headers/AllInstructorsHeader";
import InstructorFilters from "@/components/molecules/table-filters/InstructorFilters";
import Table from "@/components/molecules/table/Table";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useTableState } from "@/hooks/useTableState";
import Instructor, {
  InstructorFilters as InstructorSearchFilters,
} from "@/interfaces/Instructor";
import { useGetInstructorsQuery } from "@/services/instructors";
import { faEdit, faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColumnDef, TableState, Updater } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { FC, useCallback, useRef } from "react";
import { Link } from "react-router-dom";

const InstructorsView: FC = () => {
  const [state, setState] = useTableState<
    Partial<TableState> &
      Pick<InstructorSearchFilters, "areas" | "courseIds" | "area" | "courseId">
  >("instructors", {
    pagination: {
      pageIndex: 0,
      pageSize: 15,
    },
    globalFilter: "",
  });

  const debouncedQuery = useDebouncedValue(state.globalFilter, 500);

  const onStateChange = useCallback(
    (updater: Updater<Partial<TableState>>) => {
      setState((old) => {
        const next = typeof updater === "function" ? updater(old) : updater;
        return next.globalFilter !== old.globalFilter
          ? {
              ...next,
              pagination: {
                pageSize: old.pagination?.pageSize ?? 15,
                pageIndex: 0,
              },
            }
          : next;
      });
    },
    [setState],
  );

  const { data, isFetching } = useGetInstructorsQuery({
    ...state.pagination,
    query: debouncedQuery,
    // Restore links and saved filters from before multiple selection was available.
    areas: state.areas ?? (state.area ? [state.area] : undefined),
    courseIds:
      state.courseIds ?? (state.courseId ? [state.courseId] : undefined),
    ...(state.sorting?.[0] &&
      ["fullname", "email", "phone", "createdAt"].includes(
        state.sorting[0].id,
      ) && {
        sortBy: state.sorting[0].id,
        sortDirection: state.sorting[0].desc ? "desc" : "asc",
      }),
  });

  const columns = useRef<ColumnDef<Instructor>[]>([
    {
      header: "Actions",
      enableSorting: false,
      footer: (props) => props.column.id,
      accessorFn: ({ id }) => (
        <div>
          <Link to={`/instructors/${id}`} className="btn btn-primary btn-sm">
            <FontAwesomeIcon icon={faEye} />
          </Link>
          <Link
            to={`/instructors/${id}/edit`}
            className="btn btn-info btn-sm ms-2"
          >
            <FontAwesomeIcon icon={faEdit} />
          </Link>
        </div>
      ),
      id: "actions",
      cell: (info) => info.getValue(),
    },
    {
      header: "Zones géographiques",
      enableSorting: false,
      footer: (props) => props.column.id,
      accessorFn: ({ areas }) => {
        return (
          areas &&
          areas.length > 0 &&
          areas.map((areas, index) => (
            <span
              key={index}
              className="badge border link-secondary text-decoration-none me-1"
            >
              {areas}
            </span>
          ))
        );
      },
      id: "areas",
      cell: (info) => info.getValue(),
    },
    {
      header: "Nom complet",
      footer: (props) => props.column.id,
      accessorFn: ({ fullname }) => fullname,
      id: "fullname",
      cell: (info) => info.getValue(),
    },
    {
      header: "Email",
      footer: (props) => props.column.id,
      accessorFn: ({ email }) => email,
      id: "email",
      cell: (info) => info.getValue(),
    },
    {
      header: "Numéro\xa0de\xa0téléphone",
      footer: (props) => props.column.id,
      accessorFn: ({ phone }) =>
        phone ? <PhoneCallLink number={phone} /> : <UnknownBadge />,
      id: "phone",
      cell: (info) => info.getValue(),
    },
    {
      header: "Crée le",
      footer: (props) => props.column.id,
      accessorFn: (row) =>
        DateTime.fromISO(row.createdAt).toFormat("dd-MM-yyyy"),
      id: "createdAt",
      cell: (info) => info.getValue(),
    },
    {
      header: "Formations",
      enableSorting: false,
      footer: (props) => props.column.id,
      accessorFn: ({ courses }) => {
        return (
          courses &&
          courses.length > 0 &&
          courses.map((course, index) => (
            <span
              key={index}
              className="badge border link-secondary text-decoration-none me-1"
            >
              {course.name}
            </span>
          ))
        );
      },
      id: "courses",
      cell: (info) => info.getValue(),
    },
  ]).current;

  return (
    <div className="formalys-page formalys-list-page">
      <Table
        resource="instructors"
        HeaderComponent={AllInstructorsHeader}
        FilterComponent={InstructorFilters}
        smallFilters
        columns={columns}
        state={state}
        tableName="instructors"
        onStateChange={onStateChange}
        isLoading={isFetching}
        data={data?.data}
        meta={data?.meta}
      />
    </div>
  );
};

export default InstructorsView;
